import { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../services/firebase";
import { getUserProfile } from "../services/authService";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          // --- LOGGED IN USER FLOW ---
          setUser(firebaseUser);

          // Fetch profile and token result in parallel
          const results = await Promise.allSettled([
            getUserProfile(firebaseUser.uid),
            firebaseUser.getIdTokenResult()
          ]);

          if (results[0].status === "fulfilled") setProfile(results[0].value);
          if (results[1].status === "fulfilled") {
            setIsAdmin(results[1].value.claims.role === "admin");
          }
        } else {
          // --- GUEST / PUBLIC FLOW ---
          setUser(null);
          setProfile(null);
          setIsAdmin(false);
        }
      } catch (error) {
        console.error("Auth helper error:", error);
      } finally {
        // This MUST run for both guests and logged-in users
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const value = { user, profile, isAdmin, loading };

  return (
    <AuthContext.Provider value={value}>
      {/* If we are still "Loading" the very first auth check, 
          show a dark screen with a subtle hint. 
      */}
      {loading ? (
        <div style={{ background: "#070910", height: "100vh" }} />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}